import React, { useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { UseStores } from '../../../hooks/UseStores';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import ZipcodeInfo from './ZipCodeInfo';
import MarketInfo from './MarketInfo';
import SelectionPanel from './SelectionPanel';
import SelectedZipcodesList from './SelectedZipcodesList';
import MarketSelect from './MarketSelect';
import Map from './Map';
import useMarketOverviewMode from '../../../hooks/useMarketOverviewMode';

const ZipcodesSelectionWidget = ({
  markets,
  allMarkets,
  selectedProdcode,
  selectedZipcodes, // Record<zipcode, Demographics>
  demographics,
  quote,
  bbox,
  onSelectMarketInOverview,
  onSelectMarket, // (prodcode: number, prodcodeToZoom: number | undefined)
  onSelectFeature, // (zipcode: number, isSelected: boolean | undefined) => void ("isSelected === undefined" means toggle)
}) => {
  const { audience } = UseStores();
  const [hoveredZipcode, setHoveredZipcode] = useState(null);
  const [hoveredMarketId, setHoveredMarketId] = useState(null);

  const [isOverviewMode, handleZoomChange] = useMarketOverviewMode(
    audience.selectedAudience.label,
    onSelectMarket,
    selectedProdcode,
  );

  const handleSelectMarket = useCallback(
    (prodcode) => {
      onSelectMarket(prodcode, prodcode);
    },
    [onSelectMarket],
  );

  const demographicsById = useMemo(
    () =>
      demographics.reduce((res, demogr) => {
        res[demogr.zip] = demogr;
        return res;
      }, {}),
    [demographics],
  );

  const marketsById = useMemo(
    () =>
      markets.reduce((res, market) => {
        res[market.prodcode] = market;
        return res;
      }, {}),
    [markets],
  );

  const styles = {
    mapContainer: {
      display: 'flex',
      marginTop: { sm: '-7vh' },
    },
    helpText: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
    },
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={5.5} md={4}>
          <MarketSelect onChange={onSelectMarketInOverview} />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={5.5} md={4} order={{ xs: 2, sm: 1 }}>
          {Object.keys(selectedZipcodes).length === 0 && (
            <div style={styles.helpText}>
              <Typography variant="body1" gutterBottom>
                Click on the map to select the zip codes you want to reach.
              </Typography>
            </div>
          )}
          <SelectionPanel title={audience.selectedAudience.value} quote={quote}>
            <SelectedZipcodesList
              selectedZipcodes={selectedZipcodes}
              onSelectZipcode={onSelectFeature}
            />
          </SelectionPanel>
        </Grid>
        <Grid
          item
          xs={12}
          sm={6.5}
          md={8}
          order={{ xs: 1, sm: 2 }}
          sx={styles.mapContainer}
        >
          {!isOverviewMode &&
            hoveredZipcode &&
            demographicsById[hoveredZipcode] && (
              <div className="hoverInfoPanelWrapper">
                <ZipcodeInfo
                  demographics={demographicsById[hoveredZipcode]}
                  zipcodeTitle={demographicsById[hoveredZipcode].zip}
                  cost={demographicsById[hoveredZipcode].price_formatted}
                  addresses={demographicsById[hoveredZipcode].total_households}
                  isAdded={!!selectedZipcodes?.[hoveredZipcode]}
                  onToggle={() => {
                    onSelectFeature(hoveredZipcode);
                  }}
                />
              </div>
            )}
          {isOverviewMode &&
            hoveredMarketId &&
            marketsById[hoveredMarketId] && (
              <div className="hoverInfoPanelWrapper">
                <MarketInfo
                  marketTitle={marketsById[hoveredMarketId].market}
                  zipCount={marketsById[hoveredMarketId].total_zips}
                  showZoomButton={true}
                  onClickZoom={() => {}}
                />
              </div>
            )}
          <Map
            mode="hover"
            selectedZipcodes={selectedZipcodes}
            demographics={demographics}
            bbox={bbox}
            availableMarkets={allMarkets}
            hoveredZipcode={hoveredZipcode}
            hoveredMarketId={hoveredMarketId}
            onHoverZipcode={setHoveredZipcode}
            onHoverMarket={setHoveredMarketId}
            onSelectZipcode={onSelectFeature}
            onSelectMarket={handleSelectMarket}
            onZoomChange={handleZoomChange}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default observer(ZipcodesSelectionWidget);
